import { useMemo } from 'react';
import { useAngularContext } from 'AngularContext';
import { type AnyObject } from '@Types';
import { useCurrentUser, useDateHelper } from 'FrontRoyalAngular';
import { getCohort } from 'Users';
import { useStudentDashboardContent } from 'StudentDashboardContent';
import { useTranslation } from 'react-i18next';
import { type StreamForDashboard } from 'Lessons';
import { getPeriodLearnerProjects } from 'LearnerProjects';
import { getCurrentPeriodDates, createTitleFromPeriod } from './helpers';

// oxlint-disable-next-line max-lines-per-function
export function useScheduleBox() {
    const { t } = useTranslation('back_royal', { keyPrefix: 'scheduleBox' });
    const $injector = useAngularContext();
    const formatsText = $injector.get<AnyObject<(text: string) => string>>('FormatsText');
    const currentUser = useCurrentUser();
    const dateHelper = useDateHelper();
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);

    const { lessonStreamsByLocalePackId } = useStudentDashboardContent();

    const currentPeriod = useMemo(() => {
        if (!cohort || cohort?.currentPeriodIndex == null) return null;
        return cohort.periods[cohort.currentPeriodIndex];
    }, [cohort]);

    const currentPeriodDates = useMemo(() => {
        if (!cohort) return null;
        return getCurrentPeriodDates(cohort, dateHelper);
    }, [cohort, dateHelper]);

    const currentPeriodTitle = useMemo(() => {
        if (!currentPeriod || cohort?.currentPeriodIndex == null) return null;
        return createTitleFromPeriod(currentPeriod, formatsText, cohort.currentPeriodIndex) || t('fallbackHeading');
    }, [currentPeriod, formatsText, cohort?.currentPeriodIndex, t]);

    const formattedCurrentPeriodDates = useMemo(() => {
        if (!currentPeriodDates) return null;
        return dateHelper.formattedDateRange(currentPeriodDates.startDate, currentPeriodDates.endDate);
    }, [currentPeriodDates, dateHelper]);

    const requiredCourses = useMemo(() => {
        if (!currentPeriod || !lessonStreamsByLocalePackId) return [];
        return currentPeriod.streamEntries
            .filter(entry => entry.required)
            .map(entry => lessonStreamsByLocalePackId[entry.localePackId])
            .filter((stream): stream is StreamForDashboard => !!stream);
    }, [currentPeriod, lessonStreamsByLocalePackId]);

    const optionalCourses = useMemo(() => {
        if (!currentPeriod || !lessonStreamsByLocalePackId) return [];
        return currentPeriod.streamEntries
            .filter(entry => !entry.required)
            .map(entry => lessonStreamsByLocalePackId[entry.localePackId])
            .filter((stream): stream is StreamForDashboard => !!stream);
    }, [currentPeriod, lessonStreamsByLocalePackId]);

    const learnerProjects = useMemo(() => {
        if (!cohort || !currentPeriod) return [];
        return getPeriodLearnerProjects(cohort, currentPeriod);
    }, [cohort, currentPeriod]);

    return {
        currentPeriodTitle,
        currentPeriodDescription: currentPeriod?.description || '',
        formattedCurrentPeriodDates,
        learnerProjects,
        scheduleUrl: cohort?.externalScheduleUrl || '',
        requiredCourses,
        optionalCourses,
    };
}
